import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getShopReturns, updateShopReturnStatus, createShopReturnLabel, toAbsUrl, getMyShopProducts } from "../utils/api";
import "../styles/shopReturns.css";

function normalizeReturnsResponse(data) {
  const list =
    (Array.isArray(data?.returns) && data.returns) ||
    (Array.isArray(data?.items) && data.items) ||
    (Array.isArray(data?.data) && data.data) ||
    [];
  return list;
}

// ✅ Removed local absUrl, using toAbsUrl from utils instead

function extractImgValue(val) {
  if (!val) return "";
  if (typeof val === "string") {
    const s = String(val).trim();
    if (!s || s === "[object Object]") return "";
    if (s.startsWith("[") || s.startsWith("{")) {
      try {
        const parsed = JSON.parse(s);
        return extractImgValue(parsed);
      } catch {
        return "";
      }
    }
    return s;
  }
  if (Array.isArray(val)) {
    for (const item of val) { const s = extractImgValue(item); if (s) return s; }
    return "";
  }
  if (typeof val === "object") {
    const direct = String(
      val.url || val.secure_url || val.path || val.image || val.src || ""
    ).trim();
    if (direct) return direct;

    const publicId = String(val.public_id || "").trim();
    if (publicId) {
      const resourceType = String(val.resource_type || "image").trim() || "image";
      const format = String(val.format || "").trim();
      const withExt = format && !/\.[a-z0-9]{2,6}$/i.test(publicId)
        ? `${publicId}.${format}`
        : publicId;
      return `https://res.cloudinary.com/dohetomaw/${resourceType}/upload/${withExt}`;
    }

    return "";
  }
  return String(val || "").trim();
}

function pickFirstImage(obj) {
  if (!obj) return "";
  if (typeof obj === "string") return extractImgValue(obj);
  if (Array.isArray(obj)) return extractImgValue(obj);
  const images = obj.images;
  if (typeof images === "string") return extractImgValue(images);
  if (Array.isArray(images) && images.length) return extractImgValue(images);
  return extractImgValue(obj.image || obj.imageUrl || obj.productImage || obj.thumbnail || obj.thumb || "");
}

function pickReturnProductTitle(r) {
  return (
    r?._ui?.productTitle ||
    r?.productTitle ||
    r?.productId?.title ||
    r?.productId?.name ||
    r?.orderId?.productTitle ||
    r?.orderId?.productName ||
    r?.orderId?.product?.title ||
    r?.orderId?.product?.name ||
    "(deleted product)"
  );
}

function normalizeTitleKey(v) {
  return String(v || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

function pickReturnProductImage(r) {
  const direct =
    extractImgValue(r?._ui?.productImage) ||
    extractImgValue(r?.productImage) ||
    extractImgValue(r?.productImg) ||
    extractImgValue(r?.image) ||
    extractImgValue(r?.imageUrl) ||
    extractImgValue(r?.orderId?.productImage) ||
    extractImgValue(r?.orderId?.productImg) ||
    extractImgValue(r?.orderId?.image) ||
    extractImgValue(r?.orderId?.imageUrl) ||
    extractImgValue(r?.orderId?.lineItem?.image) ||
    "";

  const fromProduct =
    pickFirstImage(r?.productId) ||
    pickFirstImage(r?.product) ||
    pickFirstImage(r?.orderId?.product) ||
    "";

  const orderLine =
    pickFirstImage(r?.orderItem) ||
    pickFirstImage(r?.orderLine) ||
    pickFirstImage(r?.item) ||
    "";

  const orderItems = Array.isArray(r?.orderId?.items) ? r.orderId.items : [];
  let fromOrderItems = "";
  for (const it of orderItems) {
    const img =
      extractImgValue(it?.productImage) ||
      extractImgValue(it?.image) ||
      extractImgValue(it?.imageUrl) ||
      extractImgValue(it?.thumbnail) ||
      pickFirstImage(it?.product) ||
      "";
    if (img) {
      fromOrderItems = img;
      break;
    }
  }

  return direct || fromProduct || orderLine || fromOrderItems || "";
}

function pickOrderId(r) {
  const o = r?.orderId;
  if (!o) return "";
  if (typeof o === "string") return o;
  return o?._id || o?.id || "";
}

function pickOrder(r) {
  const o = r?.orderId;
  if (!o || typeof o === "string") return null;
  return o;
}

function pickReturnId(r) {
  return r?._id || r?.id || "";
}

function pickQty(r) {
  return r?.quantity || r?.qty || r?.orderItem?.quantity || r?.orderLine?.quantity || 0;
}

function pickProductIdForReturn(r) {
  const direct = r?.productId;
  if (typeof direct === "string") return String(direct || "").trim();
  if (direct && typeof direct === "object") {
    const id = direct._id || direct.id;
    if (id) return String(id).trim();
  }

  const fromOrder = r?.orderId?.productId || r?.orderId?.product;
  if (typeof fromOrder === "string") return String(fromOrder || "").trim();
  if (fromOrder && typeof fromOrder === "object") {
    const id = fromOrder._id || fromOrder.id;
    if (id) return String(id).trim();
  }

  const fromLine = r?.orderItem?.productId || r?.orderLine?.productId || r?.item?.productId;
  if (typeof fromLine === "string") return String(fromLine || "").trim();
  if (fromLine && typeof fromLine === "object") {
    const id = fromLine._id || fromLine.id;
    if (id) return String(id).trim();
  }

  return "";
}

export default function ShopReturns() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [returnsList, setReturnsList] = useState([]);
  const [brokenImgByReturn, setBrokenImgByReturn] = useState({});
  const [updatingId, setUpdatingId] = useState("");
  const [filter, setFilter] = useState("");
  const [openReturnId, setOpenReturnId] = useState(null);
  const [notesById, setNotesById] = useState({}); // optional note to the buyer, sent with the next status

  // Cache product images across filter changes — no need to re-fetch on every tab switch
  const productImageCacheRef = useRef({ byId: {}, byTitle: {}, loaded: false });

  const loadProductImageCache = useCallback(async () => {
    if (productImageCacheRef.current.loaded) return productImageCacheRef.current;
    try {
      const productsData = await getMyShopProducts({ page: 1, limit: 500 });
      const products = Array.isArray(productsData?.products) ? productsData.products
        : Array.isArray(productsData?.data) ? productsData.data
        : Array.isArray(productsData) ? productsData : [];

      const byId = {};
      const byTitle = {};
      for (const p of products) {
        const pid = String(p?._id || p?.id || "").trim();
        const pImg = pickFirstImage(p);
        if (pid && pImg) byId[pid] = pImg;
        const tKey = normalizeTitleKey(p?.title || p?.name || "");
        if (pImg && tKey && !byTitle[tKey]) byTitle[tKey] = pImg;
      }
      productImageCacheRef.current = { byId, byTitle, loaded: true };
    } catch {
      productImageCacheRef.current = { byId: {}, byTitle: {}, loaded: true };
    }
    return productImageCacheRef.current;
  }, []);

  const loadReturns = useCallback(async () => {
    setLoading(true);
    setError("");

    const status = String(filter || "").trim().toLowerCase();

    try {
      const data = await getShopReturns(filter ? { status } : {});
      const list = normalizeReturnsResponse(data);

      // Load product image cache once, reuse on filter changes
      const { byId: productImageById, byTitle: productImageByTitle } = await loadProductImageCache();

      const enhanced = list.map((r) => {
        const pid = pickProductIdForReturn(r);
        const titleKey = normalizeTitleKey(pickReturnProductTitle(r));
        const current = pickReturnProductImage(r);
        const fallbackImg = (pid ? productImageById[pid] : "") || (titleKey ? productImageByTitle[titleKey] : "");
        const imageCandidates = [current, fallbackImg].filter(Boolean);
        const uniqueCandidates = [...new Set(imageCandidates)];
        const finalImg = uniqueCandidates[0] || "";
        if (!finalImg) return r;

        return {
          ...r,
          _ui: {
            ...(r?._ui || {}),
            productImage: finalImg,
            productImageCandidates: uniqueCandidates,
          },
        };
      });

      setBrokenImgByReturn({});
      setReturnsList(enhanced);
    } catch (err) {
      setError(err?.message || "Failed to load returns");
      setReturnsList([]);
    } finally {
      setLoading(false);
    }
  }, [filter, loadProductImageCache]);

  useEffect(() => {
    loadReturns();
  }, [filter, loadReturns]);

  const setStatus = async (returnId, status) => {
    if (!returnId) return;
    const next = String(status || "").trim().toLowerCase();
    setUpdatingId(returnId);
    setError("");
    try {
      await updateShopReturnStatus(returnId, next, notesById[returnId] || "");
      setNotesById((m) => ({ ...m, [returnId]: "" }));
      await loadReturns();
    } catch (err) {
      setError(err?.message || "Failed to update return");
    } finally {
      setUpdatingId("");
    }
  };

  const genLabel = async (returnId) => {
    if (!returnId) return;
    setUpdatingId(returnId);
    setError("");
    try {
      await createShopReturnLabel(returnId);
      await loadReturns();
    } catch (err) {
      setError(err?.message || "Failed to generate return label");
    } finally {
      setUpdatingId("");
    }
  };

  return (
    <div className="page-responsive max-w-5xl mx-auto min-h-screen shop-returns">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Returns</h1>
          <p className="text-gray-400 text-sm">Manage return requests from customers</p>
        </div>
        <button 
          onClick={loadReturns} 
          disabled={loading}
          className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-200 rounded-lg text-sm font-medium transition-colors border border-gray-700"
        >
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {error && (
        <div className="bg-red-900/20 text-red-400 p-4 rounded-lg mb-6 text-sm border border-red-900/30">
          {error}
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2 scrollbar-none">
        {["", "requested", "approved", "shipped_back", "received", "refunded", "rejected"].map(opt => (
          <button
            key={opt}
            onClick={() => setFilter(opt)}
            className={`px-4 py-2 rounded-full text-xs font-semibold capitalize whitespace-nowrap transition-all ${
              filter === opt 
                ? "bg-purple-600 text-white shadow-lg shadow-purple-900/20" 
                : "bg-gray-800 text-gray-400 hover:bg-gray-700 border border-gray-700/50"
            }`}
          >
            {opt || "All Returns"}
          </button>
        ))}
      </div>

      {loading && returnsList.length === 0 ? (
        <div className="text-center py-12 text-gray-500">Loading returns...</div>
      ) : returnsList.length === 0 ? (
        <div className="text-center py-16 bg-gray-800/50 rounded-xl border border-gray-700/50">
          <div className="text-4xl mb-4 opacity-50">📦</div>
          <h3 className="text-lg font-medium text-gray-200">No returns found</h3>
          <p className="text-gray-500 text-sm mt-1">
            {filter ? `No returns with status "${filter}"` : "You haven't received any return requests yet."}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {returnsList.map((r) => {
            const rid = r._id;
            const status = String(r.status || "requested").toLowerCase();
            const isUpdating = updatingId === rid;
            const isOpen = String(openReturnId) === String(rid);

            const buyerName = r.userId && typeof r.userId === "object"
              ? `${r.userId.firstName || ""} ${r.userId.lastName || ""}`.trim() || r.userId.email
              : r.buyerName || r.buyerEmail || "Unknown";

            const productTitle = pickReturnProductTitle(r);
            const productImgRaw = pickReturnProductImage(r);
            const fallbackCandidates = Array.isArray(r?._ui?.productImageCandidates)
              ? r._ui.productImageCandidates
              : [];
            const mergedCandidates = [...new Set([
              ...fallbackCandidates,
              productImgRaw,
              r?._ui?.productImage,
              r?.productImage,
            ].filter(Boolean))];

            const brokenCount = Number(brokenImgByReturn[String(rid)] || 0);
            const currentCandidate = mergedCandidates[brokenCount] || "";
            const showImg = currentCandidate ? toAbsUrl(currentCandidate) : "";
            const showBroken = brokenCount >= mergedCandidates.length;
            // Show what the buyer actually gets back (after return policy deductions) once known.
            const netRefund = r?.refundBreakdown && Number(r.refundBreakdown.net) > 0 ? Number(r.refundBreakdown.net) : null;
            const total = netRefund ?? Number(r.refundAmount || r.orderId?.total || r.orderTotal || 0);
            const currency = String(r.currency || r.orderId?.currency || "USD").toUpperCase();
            const orderId = pickOrderId(r);
            const order = pickOrder(r);
            const orderStatus = String(order?.status || r.orderStatus || "").toLowerCase();
            const orderTotal = Number(order?.total || 0);
            const orderQty = Number(order?.quantity || 0);
            const orderPrice = Number(order?.price || 0);
            const buyerEmail =
              (r.userId && typeof r.userId === "object" && r.userId.email) ||
              r.buyerEmail ||
              "";
            const returnId = pickReturnId(r);
            const qty = orderQty || pickQty(r);
            const createdAt = r.createdAt ? new Date(r.createdAt).toLocaleString() : "—";
            const updatedAt = r.updatedAt ? new Date(r.updatedAt).toLocaleString() : "—";

            // Status Badge Color
            const statusColors = {
              requested: "bg-orange-500/10 text-orange-400 border-orange-500/20",
              approved: "bg-blue-500/10 text-blue-400 border-blue-500/20",
              shipped_back: "bg-purple-500/10 text-purple-400 border-purple-500/20",
              received: "bg-indigo-500/10 text-indigo-400 border-indigo-500/20",
              refunded: "bg-green-500/10 text-green-400 border-green-500/20",
              rejected: "bg-red-500/10 text-red-400 border-red-500/20",
            };
            const badgeClass = statusColors[status] || "bg-gray-500/10 text-gray-400 border-gray-500/20";

            return (
              <div
                key={rid}
                className="bg-gray-800/40 border border-gray-700/50 rounded-xl p-5 hover:bg-gray-800/60 transition-colors cursor-pointer"
                role="button"
                tabIndex={0}
                onClick={() => setOpenReturnId(isOpen ? null : rid)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setOpenReturnId(isOpen ? null : rid);
                  }
                }}
              >
                <div className="flex flex-col sm:flex-row gap-5 justify-between">
                  {/* Left: Product & Info */}
                  <div className="flex gap-4 flex-1 min-w-0">
                    <div className="w-20 h-20 bg-gray-900 rounded-lg overflow-hidden flex-shrink-0 border border-gray-700/50">
                      {showImg && !showBroken ? (
                        <img
                          src={showImg}
                          alt=""
                          className="w-full h-full object-cover"
                          onError={() => {
                            const key = String(rid);
                            setBrokenImgByReturn((prev) => ({
                              ...prev,
                              [key]: Number(prev[key] || 0) + 1,
                            }));
                          }}
                        />
                      ) : (
                        <div className="w-full h-full grid place-items-center text-gray-600 text-xs">No Img</div>
                      )}
                    </div>
                    <div className="flex flex-col justify-center min-w-0">
                      <h4 className="font-semibold text-gray-100 text-base mb-1 truncate pr-4">{productTitle}</h4>
                      {Array.isArray(r.items) && r.items.length > 0 ? (
                        <ul className="text-xs text-gray-400 mb-1 list-disc pl-4">
                          {r.items.map((it, i) => {
                            const opts = it?.selectedOptions && typeof it.selectedOptions === "object"
                              ? Object.values(it.selectedOptions).filter(Boolean).join(" · ")
                              : "";
                            return (
                              <li key={i} className="truncate">
                                {(it?.title || "Item")}{it?.qty > 1 ? ` ×${it.qty}` : ""}{opts ? ` (${opts})` : ""}
                              </li>
                            );
                          })}
                        </ul>
                      ) : null}
                      <div className="text-sm text-gray-400 mb-2">
                        Buyer: <span className="text-gray-300 font-medium cursor-pointer hover:text-purple-300 transition-colors" onClick={(e) => {
                          e.stopPropagation();
                          const uid = typeof r.userId === "object" ? String(r.userId._id || r.userId.id || "") : String(r.userId || "");
                          if (uid) nav(`/shop/customers?userId=${uid}`);
                        }}>{buyerName}</span>
                        {buyerEmail ? <span className="text-gray-500"> · {buyerEmail}</span> : null}
                      </div>
                      
                      <div className="flex flex-wrap gap-2 items-center">
                        <span className={`px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-wide border ${badgeClass}`}>
                          {status.replace(/_/g, " ")}
                        </span>
                        <span className="text-xs text-gray-500">
                          {r.createdAt && new Date(r.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Right: Amount & Actions */}
                  <div className="flex flex-col items-end gap-3 justify-center min-w-[140px] pl-4 border-l border-gray-700/30">
                     <div className="text-lg font-bold text-gray-100">
                       {currency} {total.toFixed(2)}
                     </div>

                     {!['rejected', 'refunded'].includes(status) && (
                       <input
                         type="text"
                         className="w-full px-2 py-1 rounded-lg text-xs bg-gray-800/60 border border-gray-700/50 text-gray-200"
                         placeholder="Note to buyer (optional)"
                         maxLength={2000}
                         value={notesById[rid] || ""}
                         onChange={(e) => setNotesById((m) => ({ ...m, [rid]: e.target.value }))}
                         disabled={isUpdating}
                       />
                     )}

                     <div className="flex flex-wrap justify-end gap-2 w-full">
                        {status === 'requested' && (
                          <>
                            <Btn label="Approve" onClick={() => setStatus(rid, 'approved')} disabled={isUpdating} primary />
                            <Btn label="Reject" onClick={() => setStatus(rid, 'rejected')} disabled={isUpdating} danger />
                          </>
                        )}

                        {/* Return shipping label (buyer -> shop) */}
                        {!['rejected', 'refunded'].includes(status) && !r?.returnShipping?.labelUrl && (
                          <Btn
                            label={isUpdating ? "Creating…" : "Generate Return Label"}
                            onClick={() => genLabel(rid)}
                            disabled={isUpdating}
                            primary
                          />
                        )}
                        {r?.returnShipping?.labelUrl && (
                          <a
                            href={r.returnShipping.labelUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 hover:bg-indigo-500/20 transition-colors"
                          >
                            🏷️ Return Label{r.returnShipping.trackingNumber ? ` · ${r.returnShipping.carrierCode || ""} ${r.returnShipping.trackingNumber}` : ""}
                          </a>
                        )}

                        {['approved', 'shipped_back'].includes(status) && (
                           <Btn label="Mark Received" onClick={() => setStatus(rid, 'received')} disabled={isUpdating} primary />
                        )}

                        {status === 'received' && (
                          <div className="flex flex-col items-end gap-1">
                            <Btn label="Mark as Refunded" onClick={() => setStatus(rid, 'refunded')} disabled={isUpdating} refund />
                            <span className="text-[10px] text-gray-500 text-right">Refund (card + MSC) is processed automatically</span>
                          </div>
                        )}
                     </div>
                  </div>
                </div>

                {/* Transaction Info */}
                {isOpen && (
                  <div className="mt-4 pt-4 border-t border-gray-700/50 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-xs">
                    <div className="bg-gray-900/40 border border-gray-700/50 rounded-lg p-3">
                      <div className="text-gray-500 font-semibold uppercase tracking-wide mb-1">Return ID</div>
                      <div className="text-gray-200 font-medium break-all">{returnId || "—"}</div>
                    </div>
                    <div className="bg-gray-900/40 border border-gray-700/50 rounded-lg p-3">
                      <div className="text-gray-500 font-semibold uppercase tracking-wide mb-1">Order ID</div>
                      <div className="text-gray-200 font-medium break-all">{orderId || "—"}</div>
                    </div>
                    <div className="bg-gray-900/40 border border-gray-700/50 rounded-lg p-3">
                      <div className="text-gray-500 font-semibold uppercase tracking-wide mb-1">Order Status</div>
                      <div className="text-gray-200 font-medium">{orderStatus || "—"}</div>
                    </div>
                    <div className="bg-gray-900/40 border border-gray-700/50 rounded-lg p-3">
                      <div className="text-gray-500 font-semibold uppercase tracking-wide mb-1">Quantity</div>
                      <div className="text-gray-200 font-medium">{qty || 0}</div>
                    </div>
                    <div className="bg-gray-900/40 border border-gray-700/50 rounded-lg p-3">
                      <div className="text-gray-500 font-semibold uppercase tracking-wide mb-1">Unit Price</div>
                      <div className="text-gray-200 font-medium">{currency} {orderPrice.toFixed(2)}</div>
                    </div>
                    <div className="bg-gray-900/40 border border-gray-700/50 rounded-lg p-3">
                      <div className="text-gray-500 font-semibold uppercase tracking-wide mb-1">Order Total</div>
                      <div className="text-gray-200 font-medium">{currency} {orderTotal.toFixed(2)}</div>
                    </div>
                    <div className="bg-gray-900/40 border border-gray-700/50 rounded-lg p-3">
                      <div className="text-gray-500 font-semibold uppercase tracking-wide mb-1">Refund Amount</div>
                      <div className="text-gray-200 font-medium">{currency} {total.toFixed(2)}</div>
                    </div>
                    <div className="bg-gray-900/40 border border-gray-700/50 rounded-lg p-3">
                      <div className="text-gray-500 font-semibold uppercase tracking-wide mb-1">Buyer Email</div>
                      <div className="text-gray-200 font-medium break-all">{buyerEmail || "—"}</div>
                    </div>
                    <div className="bg-gray-900/40 border border-gray-700/50 rounded-lg p-3">
                      <div className="text-gray-500 font-semibold uppercase tracking-wide mb-1">Requested</div>
                      <div className="text-gray-200 font-medium">{createdAt}</div>
                    </div>
                    <div className="bg-gray-900/40 border border-gray-700/50 rounded-lg p-3">
                      <div className="text-gray-500 font-semibold uppercase tracking-wide mb-1">Last Update</div>
                      <div className="text-gray-200 font-medium">{updatedAt}</div>
                    </div>
                  </div>
                )}

                {/* Reason / Note */}
                {(r.reason || r.note) && (
                  <div className="mt-5 pt-4 border-t border-gray-700/50 text-sm grid gap-2">
                    {r.reason && (
                      <div className="flex gap-2 text-gray-400">
                        <span className="font-medium text-gray-500 min-w-[60px]">Reason:</span> 
                        <span className="text-gray-300">{r.reason}</span>
                      </div>
                    )}
                    {r.note && (
                      <div className="flex gap-2 text-gray-400">
                        <span className="font-medium text-gray-500 min-w-[60px]">Note:</span> 
                        <span className="text-gray-300 italic">"{r.note}"</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Btn({ label, onClick, disabled, primary, danger, refund }) {
  const base = "px-4 py-2 rounded-lg text-xs font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm";
  let variants = "bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white border border-gray-600";
  
  if (primary) variants = "bg-purple-600 text-white hover:bg-purple-500 hover:shadow-purple-900/50 border border-purple-500";
  if (refund) variants = "bg-green-600 text-white hover:bg-green-500 hover:shadow-green-900/50 border border-green-500";
  if (danger) variants = "bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/30";
  
  return (
    <button onClick={onClick} disabled={disabled} className={`${base} ${variants}`}>
      {disabled ? "..." : label}
    </button>
  );
}
